import { defineStore } from 'pinia';
import type { ListOffer } from '~/lib/goliath/offers';

// Use an object with the magic "__noSuchMethod__" function to catch all calls, when Local Storage is not available).
// @ts-ignore
const storage: Storage =
  typeof navigator !== 'undefined' &&
  navigator?.cookieEnabled &&
  typeof localStorage !== 'undefined' &&
  localStorage !== null
    ? localStorage
    : { __noSuchMethod__: () => {} };

const STORE_KEY = 'telekomLastSeenOffers';

export { STORE_KEY };

export interface LastSeenOffersState {
  offerArticleNumbers: string[];
  lastSeenFetchedOffers: ListOffer[];
  lastRemovedArticleNumber?: string;
}

export const useLastSeenOffersStore = defineStore('lastSeenOffers', {
  state: (): LastSeenOffersState => ({
    // A list of all article numbers that have been last seen
    offerArticleNumbers: [],
    // A list of the full offer objects used to display the last seen product tiles
    lastSeenFetchedOffers: [],
    lastRemovedArticleNumber: undefined,
  }),

  getters: {
    getLastSeenFetchedOffers(state) {
      return state.lastSeenFetchedOffers || [];
    },
    lastSeenOffersFromStorage(state) {
      const lastSeenOffers = state.offerArticleNumbers;
      if (lastSeenOffers.length > 0) return lastSeenOffers;
      try {
        return JSON.parse(storage.getItem(STORE_KEY) as string) || [];
      } catch (e) {
        return [];
      }
    },
    lastSeenOffersArticleNumbers(state) {
      if (!state.offerArticleNumbers) return [];
      return state.lastRemovedArticleNumber
        ? [state.lastRemovedArticleNumber, ...state.offerArticleNumbers]
        : state.offerArticleNumbers;
    },
  },

  actions: {
    initLastSeenOffers(articleNumber: string) {
      this.loadOffersFromLocalstorage();
      this.initArticleOffers(articleNumber);
    },

    loadOffersFromLocalstorage() {
      const offerArticleNumbers = this.lastSeenOffersFromStorage;
      this.setLastSeenArticleNumbers(offerArticleNumbers);
    },

    initArticleOffers(articleNumber: string) {
      const lastSeenOffers = this.offerArticleNumbers;

      // remove the item if it's in the array
      if (lastSeenOffers.includes(articleNumber)) {
        lastSeenOffers.splice(lastSeenOffers.indexOf(articleNumber), 1);
      }

      // add the item to the end of the array since it's the last seen item
      lastSeenOffers.push(articleNumber);

      // check length, if there are already enough items, remove first one
      if (lastSeenOffers.length >= 6) {
        this.lastRemovedArticleNumber = lastSeenOffers.shift();

        // remove it from lastSeenFetchedOffers
        if (
          this.lastRemovedArticleNumber === this.lastSeenFetchedOffers?.[0]?.product?.articleNumber
        ) {
          this.lastSeenFetchedOffers.shift();
        }
      }
      try {
        storage.setItem(STORE_KEY, JSON.stringify(lastSeenOffers));
      } catch (e) {
        // empty
      }
      this.offerArticleNumbers = lastSeenOffers;
    },

    setLastSeenArticleNumbers(offerArticleNumbers: string[]) {
      this.offerArticleNumbers = offerArticleNumbers || [];
    },

    addLastSeenOffers(offers: ListOffer[]) {
      if (!Array.isArray(offers) || offers.length === 0) return;

      const articleNumbers = this.lastRemovedArticleNumber
        ? [this.lastRemovedArticleNumber, ...this.offerArticleNumbers]
        : this.offerArticleNumbers;
      this.lastSeenFetchedOffers = articleNumbers
        .map((articleNumber: string) =>
          [...this.lastSeenFetchedOffers, ...offers].find(
            (offer) => offer.product?.articleNumber === articleNumber
          )
        )
        .filter(Boolean);
    },
  },
});
